import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Menu, X, User, LogOut, Shield } from 'lucide-react';
import { Button } from '../ui/button';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsOpen(false);
  };

  const navLinks = [
    { to: '/about', label: 'About' },
    { to: '/donate', label: 'Donate' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b-2 border-slate-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* LOGO */}
          <Link
            to="/"
            className="flex items-center"
            data-testid="navbar-logo"
          >
            <img
              src="/images/branding/pork-cops.png"
              alt="Pork Cops"
              className="h-10 md:h-11 w-auto"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fallback = document.getElementById('navbar-logo-fallback');
                if (fallback) fallback.style.display = 'flex';
              }}
            />

            {/* fallback logo */}
            <div
              id="navbar-logo-fallback"
              className="hidden items-center justify-center px-4 py-2 rounded-[18px] border-2 border-[#0b3f59]"
              style={{
                background: 'linear-gradient(90deg, #00FFFF, #00E5E5, #00BFCF)',
              }}
            >
              <span className="text-lg font-black uppercase text-[#2FB4E9] mr-1">
                PORK
              </span>
              <span className="text-lg font-black uppercase text-[#FF6FAF]">
                COPS
              </span>
            </div>
          </Link>

          {/* DESKTOP NAV */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-[#2FB4E9] hover:text-[#4CC3F0] uppercase font-bold tracking-widest text-sm"
              >
                {link.label}
              </Link>
            ))}

            {user ? (
              <div className="flex items-center gap-4">

                <Link to="/dashboard">
                  <Button
                    variant="ghost"
                    className="text-yellow-500 uppercase font-bold"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>
                </Link>

                {isAdmin && (
                  <Link to="/admin">
                    <Button
                      variant="ghost"
                      className="text-red-500 uppercase font-bold"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin
                    </Button>
                  </Link>
                )}

                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-slate-400 hover:text-white"
                >
                  <LogOut className="w-4 h-4" />
                </Button>

              </div>
            ) : (
              <Link to="/login">
                <Button
                  className="font-black uppercase tracking-wider text-[#041014] border-2 border-[#00FFFF]"
                  style={{
                    background: 'linear-gradient(90deg, #00FFFF, #00E5E5, #00BFCF)',
                  }}
                >
                  Sign In
                </Button>
              </Link>
            )}
          </div>

          {/* MOBILE MENU BUTTON */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X /> : <Menu />}
          </button>

        </div>
      </div>

      {/* MOBILE MENU */}
      {isOpen && (
        <div className="md:hidden bg-slate-900 border-t border-slate-700">
          <div className="px-4 py-4 space-y-3">

            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="block text-[#2FB4E9] uppercase font-bold py-2"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}

            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                  <div className="text-yellow-500 uppercase font-bold py-2">
                    Dashboard
                  </div>
                </Link>

                {isAdmin && (
                  <Link to="/admin" onClick={() => setIsOpen(false)}>
                    <div className="text-red-500 uppercase font-bold py-2">
                      Admin
                    </div>
                  </Link>
                )}

                <button
                  onClick={handleLogout}
                  className="text-slate-400 uppercase font-bold py-2 w-full text-left"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setIsOpen(false)}>
                <Button
                  className="w-full font-black uppercase text-[#041014] border-2 border-[#00FFFF]"
                  style={{
                    background: 'linear-gradient(90deg, #00FFFF, #00E5E5, #00BFCF)',
                  }}
                >
                  Sign In
                </Button>
              </Link>
            )}

          </div>
        </div>
      )}
    </nav>
  );
};
